import React, { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import { storage, db } from "../firebase"; // [NEW] Firebase Storage & Firestore
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"; // [NEW] Firebase Storage functions
import { collection, query, where, getDocs } from "firebase/firestore"; // [NEW] Firestore for team members
import { useModal } from "../contexts/ModalContext";
import { DEFAULT_THEME } from "../constants/theme";
import { formatPhoneNumber } from "../utils";
import VisitLogModal from "../components/VisitLogModal";
import TeacherEditModal from "../components/TeacherEditModal";
import BulkRegistrationModal from "../components/BulkRegistrationModal"; // [NEW]
import { ICONS, INITIAL_FORM_DATA } from "./teacher/constants";
import useDraggableModal from "./teacher/useDraggableModal";
import useTeacherListEscapeHandler from "./teacher/useTeacherListEscapeHandler";
import {
  buildFilteredTeachers,
  buildSortedMinutes,
  formatTeacherListDate,
} from "./teacher/teacherListUtils";
import { enrichRecord } from "../utils/recordUtils";
import TeacherStatsSection from "./teacher/TeacherStatsSection";
import TeacherListToolbar from "./teacher/TeacherListToolbar";
import TeacherDetailModal from "./teacher/TeacherDetailModal";
import TeacherMinuteDetailModal from "./teacher/TeacherMinuteDetailModal";
import TeacherMinuteEditModal from "./teacher/TeacherMinuteEditModal";
import TeacherMinutesSection from "./teacher/TeacherMinutesSection";
import TeacherListContentSection from "./teacher/TeacherListContentSection";

export default function TeacherListView({
  teachers,
  onAdd,
  onBulkAdd,
  onUpdate,
  onDelete,
  onBulkDelete,
  minutes,
  onAddMinute,
  onUpdateMinute,
  onDeleteMinute,
  demoAction,
  attendanceLogs,
  visitLogs,
  onNavigateToAttendance,
  onNavigateToVisit,
  onAddLog,
  onUpdateLog,
  onDeleteLog,
  selectedDepartment,
  myDepartments = {},
  userProfile = {},
  theme = DEFAULT_THEME,
  onOpenCheerModal,
  targetTeacherId = null,
  onClearTargetTeacherId,
  detailReturnMenu = null,
  onClearDetailReturnMenu,
  onNavigateMenu
}) {
  const [activeTab, setActiveTab] = useState("list");
  const [viewMode, setViewMode] = useState("list"); // 'card' or 'list'
  
  const safeTeachers = Array.isArray(teachers) ? teachers : [];
  const safeMinutes = Array.isArray(minutes) ? minutes : [];
  const { openConfirm, openAlert } = useModal();

  // [NEW] Permission check - teachers have limited access
  const isTeacher = userProfile?.memberRole === 'teacher';

  // [NEW] Calculate available departments (My Departments + '기타')
  const availableDepartments = useMemo(() => {
    let depts = [];

    // 1. Add Primary Department from Profile
    if (userProfile?.deptName) {
      depts.push(userProfile.deptName);
    }

    // 2. Add configured 'My Departments'
    if (Array.isArray(myDepartments) && myDepartments.length > 0) {
      depts = [...depts, ...myDepartments];
    } else if (myDepartments && typeof myDepartments === 'object' && Object.keys(myDepartments).length > 0) {
      depts = [...depts, ...Object.keys(myDepartments)];
    }

    // 3. Fallback only if absolutely no departments found
    if (depts.length === 0) {
      depts = ["영아부", "유아부", "유치부", "유년부", "초등부", "중등부", "고등부", "청년부", "장년부"];
    }

    return [...new Set(depts)].filter(d => d !== '기타' && d !== 'All');
  }, [myDepartments, userProfile]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 모달 상태
  const resolveTargetTeacher = () => {
    if (!targetTeacherId) return null;
    const sourceTeachers = Array.isArray(teachers) ? teachers : [];
    return sourceTeachers.find((teacher) => teacher.id === targetTeacherId) || null;
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewingTeacher, setViewingTeacher] = useState(() => resolveTargetTeacher());
  const [isDeepLinkedDetailOpen, setIsDeepLinkedDetailOpen] = useState(Boolean(targetTeacherId));
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [viewingLog, setViewingLog] = useState(null); // [NEW] Viewing log detail

  // [NEW] Visit Log Creation State
  const [logInputContent, setLogInputContent] = useState("");
  const [logInputType, setLogInputType] = useState("면담");
  const [logInputDate, setLogInputDate] = useState(new Date().toISOString().split('T')[0]);
  const [logInputSentiment, setLogInputSentiment] = useState("neutral");
  const [editingLogId, setEditingLogId] = useState(null);

  const [isMinutesModalOpen, setIsMinutesModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false); // [NEW]
  const [isRegisterMenuOpen, setIsRegisterMenuOpen] = useState(false); // [NEW] Dropdown state
  const registerMenuRef = useRef(null); // [NEW] Dropdown ref


  // [FIX] 최근 출석/심방 기록 실시간 동기화
  useEffect(() => {
    if (viewingTeacher) {
      const refreshed = safeTeachers.find(t => t.id === viewingTeacher.id);
      if (refreshed) {
        setViewingTeacher(enrichRecord(refreshed, attendanceLogs, visitLogs));
      }
    }
  }, [attendanceLogs, visitLogs, safeTeachers]);

  // [NEW] 계정 연결 관련 상태
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);

  // [NEW] 조직 멤버 목록 조회 함수
  const fetchTeamMembers = async () => {
    if (!userProfile?.organizationId) return;
    setLinkLoading(true);
    try {
      const q = query(
        collection(db, "users"),
        where("organizationId", "==", userProfile.organizationId)
      );
      const snapshot = await getDocs(q);
      const members = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
      setTeamMembers(members);
    } catch (error) {
      console.error("팀 멤버 조회 오류:", error);
    } finally {
      setLinkLoading(false);
    }
  };

  // [NEW] 계정 연결 핸들러
  const handleLinkAccount = async (memberUid, memberName) => {
    if (!viewingTeacher) return;
    try {
      await onUpdate({
        ...viewingTeacher,
        linkedUserId: memberUid,
        linkedUserName: memberName
      });
      setShowLinkPopup(false);
      openAlert('계정 연결 완료', `${viewingTeacher.name} 교사가 ${memberName}님의 계정과 연결되었습니다.`);
    } catch (error) {
      console.error("계정 연결 오류:", error);
    }
  };

  // [NEW] 계정 연결 해제 핸들러
  const handleUnlinkAccount = async () => {
    if (!viewingTeacher) return;
    openConfirm(
      '계정 연결 해제',
      `${viewingTeacher.name} 교사의 계정 연결을 해제하시겠습니까?`,
      async () => {
        try {
          await onUpdate({
            ...viewingTeacher,
            linkedUserId: null,
            linkedUserName: null
          });
          openAlert('연결 해제 완료', '계정 연결이 해제되었습니다.');
        } catch (error) {
          console.error("연결 해제 오류:", error);
        }
      }
    );
  };

  // [NEW] Modal Drag State
  const {
    pos: editModalPos,
    setPos: setEditModalPos,
    handleMouseDown: handleEditMouseDown,
  } = useDraggableModal({
    blockedSelectors: ["button", "input", "select", "textarea"],
    preventDefault: true,
  });

  const {
    pos: detailModalPos,
    setPos: setDetailModalPos,
    handleMouseDown: handleDetailMouseDown,
  } = useDraggableModal({
    blockedSelectors: ["button", "a"],
    preventDefault: true,
  });

  const {
    pos: minuteEditModalPos,
    setPos: setMinuteEditModalPos,
    handleMouseDown: handleMinuteEditMouseDown,
  } = useDraggableModal({
    blockedSelectors: ["button", "input", "textarea"],
    preventDefault: true,
  });

  const {
    pos: minuteDetailModalPos,
    setPos: setMinuteDetailModalPos,
    handleMouseDown: handleMinuteDetailMouseDown,
  } = useDraggableModal({
    blockedSelectors: ["button"],
    preventDefault: true,
  });

  const [editingMinute, setEditingMinute] = useState(null);
  const [viewingMinute, setViewingMinute] = useState(null);

  // Reset position when minute modal opens
  useEffect(() => {
    if (isMinutesModalOpen) setMinuteEditModalPos({ x: 0, y: 0 });
  }, [isMinutesModalOpen]);

  useEffect(() => {
    if (viewingMinute) setMinuteDetailModalPos({ x: 0, y: 0 });
  }, [viewingMinute]);
  // Click Outside Handler for Register Menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (registerMenuRef.current && !registerMenuRef.current.contains(event.target)) {
        setIsRegisterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // [기존 필터링 로직 유지]


  // 검색 및 필터
  const [searchTerm, setSearchTerm] = useState("");
  // [NEW] Local Department Filter (only active when Global Filter is All)
  const [departmentFilter, setDepartmentFilter] = useState("전체"); // Redundant
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortKey, setSortKey] = useState("name"); // [NEW] 정렬 키
  const [sortOrder, setSortOrder] = useState("asc"); // [NEW] 정렬 순서
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false); // [NEW] 다운로드 메뉴 상태

  // 폼 데이터
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // [NEW] Image Upload State
  const [isUploading, setIsUploading] = useState(false);

  // [NEW] Handle Image Upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const storageRef = ref(storage, `teachers/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData((prev) => ({ ...prev, photoUrl: url }));
    } catch (error) {
      console.error("Upload failed", error);
      openAlert("이미지 업로드 실패", "오류");
    } finally {
      setIsUploading(false);
    }
  };
  const [minuteFormData, setMinuteFormData] = useState({ date: new Date().toISOString().slice(0, 10), title: "", content: "", attendees: "" });

  // [NEW] Demo Action Handler (Moved after state initialization)
  useEffect(() => {
    if (demoAction === 'add') {
      // [MODIFIED] 빈 폼으로 열기 (자동 입력 제거)
      // [MODIFIED] 투어용 예시 데이터 자동 입력
      const t1 = setTimeout(() => {
        setIsEditModalOpen(true);
        setEditingTeacher(null);
        // [MODIFIED] Do not set formData directly, let Modal handle it via prop
        // setFormData({ ... });
      }, 500);

      return () => clearTimeout(t1);
    } else if (demoAction === 'demo_save_teacher') {
      // [NEW] Auto-save for Tour
      // Ensure we only save if the modal is actually open to prevent loops
      if (isEditModalOpen && formData.name) {
        onAdd(formData);
        setIsEditModalOpen(false); // Close modal ensuring it doesn't trigger again immediately
        setFormData(INITIAL_FORM_DATA); // Clear form to prevent re-submission match
        openAlert("교사가 등록되었습니다! 🎉", "완료");
      }
    }
  }, [demoAction]); // [FIX] Removed excessive dependencies that caused loops


  useEffect(() => {
    if (!targetTeacherId) return;
    const targetTeacher = resolveTargetTeacher();
    if (targetTeacher) {
      setViewingTeacher(targetTeacher);
      setIsDeepLinkedDetailOpen(true);
      if (onClearTargetTeacherId) onClearTargetTeacherId();
    }
  }, [targetTeacherId, teachers, onClearTargetTeacherId]);

  const closeViewingTeacher = () => {
    setViewingTeacher(null);
    if (isDeepLinkedDetailOpen && detailReturnMenu && onNavigateMenu) {
      onNavigateMenu(detailReturnMenu);
      if (onClearDetailReturnMenu) onClearDetailReturnMenu();
    }
    setIsDeepLinkedDetailOpen(false);
  };

  const getRelativeTime = formatTeacherListDate;

  useTeacherListEscapeHandler({
    formData,
    isEditModalOpen,
    isMinutesModalOpen,
    minuteFormData,
    openConfirm,
    setIsEditModalOpen,
    setIsMinutesModalOpen,
    setViewingLog,
    setViewingMinute,
    setViewingTeacher,
    onCloseViewingTeacher: closeViewingTeacher,
    viewingLog,
    viewingMinute,
    viewingTeacher,
  });

  const filteredTeachers = useMemo(() => {
    return buildFilteredTeachers({
      attendanceLogs,
      departmentFilter,
      searchTerm,
      selectedDepartment,
      sortKey,
      sortOrder,
      teachers: safeTeachers,
      visitLogs,
    });
  }, [safeTeachers, searchTerm, sortKey, sortOrder, attendanceLogs, visitLogs, selectedDepartment, departmentFilter]);

  const sortedMinutes = useMemo(() => {
    return buildSortedMinutes(safeMinutes);
  }, [safeMinutes]);
  const executiveTeacherCount = filteredTeachers.filter(t => t.role !== "교사").length;
  const monthlyMinuteCount = safeMinutes.filter(
    m => m.date && m.date.startsWith(new Date().toISOString().slice(0, 7))
  ).length;

  // --- 핸들러 ---
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(filteredTeachers.map(t => t.id));
    else setSelectedIds([]);
  };

  const handleSelectRow = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(sid => sid !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const handleBulkDelete = () => {
    const namesToDelete = teachers.filter(t => selectedIds.includes(t.id)).map(t => t.name);
    openConfirm(
      "일괄 삭제",
      `${selectedIds.length}명을 삭제하시겠습니까?`,
      async () => {
        if (onBulkDelete) {
          await onBulkDelete(selectedIds, namesToDelete);
        } else {
          selectedIds.forEach(id => onDelete(id));
        }
        setSelectedIds([]);
      }
    );
  };

  const handleDownloadExcel = () => {
    // [MODIFIED] 선택된 항목만 다운로드
    if (selectedIds.length === 0) {
      openAlert("다운로드할 교사를 선택해주세요.", "알림");
      return;
    }

    openConfirm("명단 다운로드", `선택한 ${selectedIds.length}명의 명단을 다운로드하시겠습니까?`, () => {
      const selectedTeachers = filteredTeachers.filter(t => selectedIds.includes(t.id));
      const data = selectedTeachers.map(t => ({
        이름: t.name,
        성별: t.gender,
        직분: t.role,
        담당: t.charge,
        연락처: t.phone,
        생년월일: t.birth,
        주소: t.address,
        비고: t.notes
      }));

      const ws = XLSX.utils.json_to_sheet(data);

      ws['!cols'] = [
        { wch: 10 }, { wch: 6 }, { wch: 8 }, { wch: 15 },
        { wch: 15 }, { wch: 12 }, { wch: 30 }, { wch: 30 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "교사명단");
      XLSX.writeFile(wb, `교사명단_선택${selectedIds.length}명_${new Date().toISOString().slice(0, 10)}.xlsx`);
    });
  };

  const handleCopyStats = () => {
    // [MODIFIED] 선택된 항목 상세 복사
    if (selectedIds.length === 0) {
      openAlert("복사할 교사를 선택해주세요.", "알림");
      return;
    }

    openConfirm("명단 복사", `선택한 ${selectedIds.length}명의 정보를 복사하시겠습니까?`, () => {
      const selectedTeachers = filteredTeachers.filter(t => selectedIds.includes(t.id));

      let text = `[📅 교사 명단] 총 ${selectedTeachers.length}명 선택\n\n`;
      selectedTeachers.forEach((t, index) => {
        text += `${index + 1}. ${t.name}\n`;
        text += `   - 성별: ${t.gender || '-'}\n`;
        text += `   - 직분: ${t.role || '-'}\n`;
        text += `   - 담당: ${t.charge || '-'}\n`;
        text += `   - 연락처: ${t.phone || '-'}\n`;
        text += `   - 생년월일: ${t.birth || '-'}\n`;
        text += `   - 주소: ${t.address || '-'}\n`;
        if (t.notes) text += `   - 비고: ${t.notes}\n`;
        text += `\n`;
      });
      text += `----------------\n(기준일: ${new Date().toLocaleDateString()})`;

      navigator.clipboard.writeText(text)
        .then(() => openAlert("명단이 복사되었습니다! 📋", "알림"))
        .catch(() => openAlert("복사 실패", "오류"));
    });
  };


  const openAddModal = () => {
    setEditingTeacher(null);
    setEditModalPos({ x: 0, y: 0 }); // Reset position
    // setFormData moved to component
    setIsEditModalOpen(true);
  };
  const openEditModal = (t) => {
    setEditingTeacher(t);
    setEditModalPos({ x: 0, y: 0 }); // Reset position
    // setFormData moved to component
    setIsEditModalOpen(true);
  };
  // handleSubmit is now passed as prop logic
  /* const handleSubmit = (e) => { ... } */
  const handleDeleteTeacher = (id) => {
    openConfirm(
      "교사 삭제",
      "정말 삭제하시겠습니까?",
      () => {
        onDelete(id);
        setViewingTeacher(null);
      }
    );
  };

  const openAddMinuteModal = () => {
    setEditingMinute(null);
    setMinuteFormData({ date: new Date().toISOString().slice(0, 10), title: "", content: "", attendees: "" });
    setIsMinutesModalOpen(true);
  };
  const openEditMinuteModal = (m) => {
    setEditingMinute(m);
    setMinuteFormData(m);
    setIsMinutesModalOpen(true);
  };
  const handleMinuteSubmit = (e) => {
    e.preventDefault();
    if (editingMinute) onUpdateMinute({ ...minuteFormData, id: editingMinute.id });
    else onAddMinute(minuteFormData);
    setIsMinutesModalOpen(false);
  };
  const handleDeleteMinute = (id) => {
    openConfirm(
      "회의록 삭제",
      "삭제하시겠습니까?",
      () => {
        onDeleteMinute(id);
        setViewingMinute(null);
      }
    );
  };

  const handleShareTeacher = (t) => {
    openConfirm("정보 공유", `${t.name} 선생님의 정보를 복사하시겠습니까?`, () => {
      const text = `[교사 정보]\n이름: ${t.name}\n직분: ${t.role}\n연락처: ${t.phone || "없음"}\n담당: ${t.charge || "없음"}`;
      navigator.clipboard.writeText(text).then(() => openAlert("교사 정보가 복사되었습니다!", "알림"));
    });
  };
  const handleShareMinute = (m) => {
    openConfirm("회의록 공유", "회의록 내용을 복사하시겠습니까?", () => {
      const text = `[회의록]\n날짜: ${m.date}\n제목: ${m.title}\n참석자: ${m.attendees}\n내용:\n${m.content}`;
      navigator.clipboard.writeText(text).then(() => openAlert("회의록 내용이 복사되었습니다!", "알림"));
    });
  };

  // --- 렌더링 함수들 ---

  // --- 메인 렌더링 ---
  return (
    <div className="w-full h-full flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 overflow-y-auto pb-20 pt-2 px-4 custom-scrollbar">
        <div className="max-w-7xl mx-auto w-full space-y-6">
          {/* 헤더 & 탭 */}
          <div className="hidden md:flex flex-col md:flex-row justify-between items-end gap-6">
            <div>
              <h2 className="text-3xl font-black text-gray-800 flex items-center gap-3 font-do-hyeon">
                <img src={ICONS.teacher} width={48} alt="icon" className="drop-shadow-md" /> 교사 명단
              </h2>
              <p className="text-gray-500 text-sm font-medium mt-1 ml-1">총 <span className="font-bold text-[#354F38]">{filteredTeachers.length}</span>명의 선생님과 함께합니다.</p>
            </div>
          </div>

          <TeacherStatsSection
            teacherCount={filteredTeachers.length}
            executiveCount={executiveTeacherCount}
            minuteCount={safeMinutes.length}
            monthlyMinuteCount={monthlyMinuteCount}
          />

          <TeacherListToolbar
            activeTab={activeTab}
            availableDepartments={availableDepartments}
            departmentFilter={departmentFilter}
            filteredTeachersLength={filteredTeachers.length}
            handleBulkDelete={handleBulkDelete}
            handleCopyStats={handleCopyStats}
            handleDownloadExcel={handleDownloadExcel}
            handleSelectAll={handleSelectAll}
            isDownloadMenuOpen={isDownloadMenuOpen}
            isMobile={isMobile}
            isRegisterMenuOpen={isRegisterMenuOpen}
            isTeacher={isTeacher}
            openAddMinuteModal={openAddMinuteModal}
            openAddModal={openAddModal}
            registerMenuRef={registerMenuRef}
            searchTerm={searchTerm}
            selectedDepartment={selectedDepartment}
            selectedIds={selectedIds}
            setActiveTab={setActiveTab}
            setDepartmentFilter={setDepartmentFilter}
            setIsBulkModalOpen={setIsBulkModalOpen}
            setIsDownloadMenuOpen={setIsDownloadMenuOpen}
            setIsRegisterMenuOpen={setIsRegisterMenuOpen}
            setSearchTerm={setSearchTerm}
            setSortKey={setSortKey}
            setSortOrder={setSortOrder}
            setViewMode={setViewMode}
            sortKey={sortKey}
            sortOrder={sortOrder}
            theme={theme}
            viewMode={viewMode}
          />

          {activeTab === "list" && (
            <TeacherListContentSection
              filteredTeachers={filteredTeachers}
              getRelativeTime={getRelativeTime}
              handleSelectRow={handleSelectRow}
              isTeacher={isTeacher}
              openAddModal={openAddModal}
              selectedIds={selectedIds}
              setViewingTeacher={(teacher) => {
                setIsDeepLinkedDetailOpen(false);
                setViewingTeacher(teacher);
              }}
              userProfile={userProfile}
              viewMode={viewMode}
              sortKey={sortKey}
              sortOrder={sortOrder}
              onSort={(key) => {
                if (sortKey === key) {
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                } else {
                  setSortKey(key);
                  setSortOrder("asc");
                }
              }}
            />
          )}

          {activeTab === "minutes" && (
            <TeacherMinutesSection
              onSelectMinute={setViewingMinute}
              sortedMinutes={sortedMinutes}
            />
          )}

          {/* 모든 모달 렌더링 호출 */}
          <TeacherEditModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSubmit={(data) => {
              if (editingTeacher) onUpdate(data);
              else onAdd(data);
              setIsEditModalOpen(false);
            }}
            teacher={editingTeacher}
            availableDepartments={availableDepartments}
            theme={theme}
            isMobile={isMobile}
            openAlert={openAlert}
            position={editModalPos}
            onHeaderMouseDown={handleEditMouseDown}
            initialData={demoAction === 'add' ? {
              name: "박지성",
              gender: "남",
              role: "교사",
              charge: "중등부 2학년",
              phone: "010-7777-7777",
              birth: "1981-02-25",
              address: "교토",
              notes: "두 개의 심장, 헌신적인 교사입니다."
            } : null}
          />

          <TeacherDetailModal
            attendanceLogs={attendanceLogs}
            detailModalPos={detailModalPos}
            editingLogId={editingLogId}
            fetchTeamMembers={fetchTeamMembers}
            handleDeleteTeacher={handleDeleteTeacher}
            handleDetailMouseDown={handleDetailMouseDown}
            handleLinkAccount={handleLinkAccount}
            handleShareTeacher={handleShareTeacher}
            handleUnlinkAccount={handleUnlinkAccount}
            isMobile={isMobile}
            isTeacher={isTeacher}
            linkLoading={linkLoading}
            logInputContent={logInputContent}
            logInputDate={logInputDate}
            logInputSentiment={logInputSentiment}
            logInputType={logInputType}
            onAddLog={onAddLog}
            onClose={closeViewingTeacher}
            onEdit={() => {
              setIsDeepLinkedDetailOpen(false);
              setViewingTeacher(null);
              setEditingTeacher(viewingTeacher);
              setFormData(viewingTeacher);
              setIsEditModalOpen(true);
            }}
            onOpenCheerModal={onOpenCheerModal}
            onUpdateLog={onUpdateLog}
            openAlert={openAlert}
            setEditingLogId={setEditingLogId}
            setLogInputContent={setLogInputContent}
            setLogInputDate={setLogInputDate}
            setLogInputSentiment={setLogInputSentiment}
            setLogInputType={setLogInputType}
            setShowLinkPopup={setShowLinkPopup}
            showLinkPopup={showLinkPopup}
            teamMembers={teamMembers}
            theme={theme}
            userProfile={userProfile}
            viewingTeacher={viewingTeacher}
            visitLogs={visitLogs}
          />
          {/* [Refactored] Teacher Edit Modal Component */}
          {/* Removed duplicate modal */}

          <BulkRegistrationModal
            isOpen={isBulkModalOpen}
            onClose={() => setIsBulkModalOpen(false)}
            type="teacher"
            defaultDepartment={(selectedDepartment && selectedDepartment !== 'All') ? selectedDepartment : (userProfile?.deptName || '')}
            onRegister={(data) => {
              if (onBulkAdd) {
                onBulkAdd(data);
              } else {
                data.forEach(item => onAdd(item));
                openAlert(`${data.length}명의 교사가 등록되었습니다.`, "완료");
              }
            }}
            openAlert={openAlert}
          />
          <TeacherMinuteDetailModal
            handleDeleteMinute={handleDeleteMinute}
            handleMinuteDetailMouseDown={handleMinuteDetailMouseDown}
            handleShareMinute={handleShareMinute}
            minuteDetailModalPos={minuteDetailModalPos}
            onClose={() => setViewingMinute(null)}
            onEdit={openEditMinuteModal}
            viewingMinute={viewingMinute}
          />
          <TeacherMinuteEditModal
            editingMinute={editingMinute}
            handleMinuteEditMouseDown={handleMinuteEditMouseDown}
            handleMinuteSubmit={handleMinuteSubmit}
            isMinutesModalOpen={isMinutesModalOpen}
            minuteEditModalPos={minuteEditModalPos}
            minuteFormData={minuteFormData}
            onClose={() => setIsMinutesModalOpen(false)}
            setMinuteFormData={setMinuteFormData}
            theme={theme}
          />

          {/* [NEW] Visit Log Detail Modal */}
          {viewingLog && (
            <VisitLogModal
              log={viewingLog}
              student={viewingTeacher} // Reuse viewingTeacher as student prop for profile picture
              onClose={() => setViewingLog(null)}
              onUpdate={(uLog) => {
                if (onUpdateLog) onUpdateLog(uLog);
                setViewingLog(null);
              }}
              onDelete={(id) => {
                if (onDeleteLog) onDeleteLog(id);
                setViewingLog(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
